/**
 * <s-sqft-calculator> — Square footage calculator for tile products.
 *
 * Converts an area in square feet into the number of pieces needed for the
 * selected variant, shows an estimated total, and can copy that count into the
 * product form's quantity input. The typed area is kept in sessionStorage per
 * product so it survives the product-form partial re-rendering on variant change.
 *
 * Attributes:
 *   data-piece-area   — Square feet covered by one piece (number).
 *   data-price        — Variant price in minor units (cents).
 *   data-step         — Quantity increment (default 1).
 *   data-min          — Quantity minimum (default 1).
 *   data-currency     — ISO currency code.
 *   data-locale       — Locale for number formatting.
 *   data-product-id   — Used to key the stored area.
 *   data-result-template  — Translated result string with {{ count }} and {{ total }}.
 *   data-applied-template — Translated confirmation with {{ count }}.
 *
 * Commands:
 *   --apply-quantity — Copies the computed count into input[name="quantity"].
 */
class SSqftCalculator extends HTMLElement {
  #ac;
  #count = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const area = this.querySelector('[data-area]');
    const waste = this.querySelector('[data-waste]');
    const stored = this.#readStored();

    if (area instanceof HTMLInputElement && stored) {
      area.value = stored.area ?? '';
      if (waste instanceof HTMLInputElement && typeof stored.waste === 'boolean') waste.checked = stored.waste;
    }

    area?.addEventListener('input', () => this.#update(), opts);
    waste?.addEventListener('change', () => this.#update(), opts);

    this.addEventListener('command', (e) => {
      if (e.command === '--apply-quantity') this.#applyQuantity();
    }, opts);

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #storageKey() {
    return `sqft-calculator:${this.dataset.productId || ''}`;
  }

  #readStored() {
    try {
      return JSON.parse(sessionStorage.getItem(this.#storageKey) || 'null');
    } catch {
      return null;
    }
  }

  #store(areaValue, wasteChecked) {
    try {
      sessionStorage.setItem(this.#storageKey, JSON.stringify({ area: areaValue, waste: wasteChecked }));
    } catch {
      /* Storage can be unavailable in private modes; the calculator still works. */
    }
  }

  #update() {
    const areaInput = this.querySelector('[data-area]');
    const wasteInput = this.querySelector('[data-waste]');
    const result = this.querySelector('[data-result]');
    const applyButton = this.querySelector('[data-apply]');
    const status = this.querySelector('[data-status]');
    if (!(areaInput instanceof HTMLInputElement) || !result) return;

    const pieceArea = Number(this.dataset.pieceArea);
    const price = Number(this.dataset.price);
    const step = Math.max(1, Number(this.dataset.step) || 1);
    const min = Math.max(1, Number(this.dataset.min) || 1);
    const areaValue = parseFloat(areaInput.value);
    const withWaste = wasteInput instanceof HTMLInputElement && wasteInput.checked;

    this.#store(areaInput.value, withWaste);
    if (status) status.textContent = '';

    if (!Number.isFinite(areaValue) || areaValue <= 0 || !(pieceArea > 0)) {
      this.#count = 0;
      result.hidden = true;
      if (applyButton) applyButton.hidden = true;
      return;
    }

    const totalArea = areaValue * (withWaste ? 1.1 : 1);
    let count = Math.ceil(totalArea / pieceArea - 1e-9);
    count = Math.max(min, Math.ceil(count / step) * step);
    this.#count = count;

    const total = this.#formatMoney((price * count) / 100);
    const template = this.dataset.resultTemplate || '';
    result.innerHTML = template
      .replace('{{ count }}', count.toLocaleString(this.dataset.locale || undefined))
      .replace('{{ total }}', total);
    result.hidden = false;
    if (applyButton) applyButton.hidden = false;
  }

  #applyQuantity() {
    if (!this.#count) return;
    const form = this.closest('form');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    quantityInput.value = String(this.#count);
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    const status = this.querySelector('[data-status]');
    if (status) status.textContent = (this.dataset.appliedTemplate || '').replace('{{ count }}', String(this.#count));
  }

  #formatMoney(amount) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('s-sqft-calculator')) {
  customElements.define('s-sqft-calculator', SSqftCalculator);
}
